import { isCometBrowser, isCometMobileBrowser } from '#pplx/frontend/comet/client/utils.js';
import { logger } from '#pplx/frontend/logger/browser.js';
/**
 * Facade for MCP operations that abstracts the difference between
 * mobile proxy and agent extension implementations.
 */
export class McpAdapter {
    mobileProxy;
    cometAdapter;
    _shouldUseMobileProxyForMCP = null;
    constructor(mobileProxy, cometAdapter) {
        this.mobileProxy = mobileProxy;
        this.cometAdapter = cometAdapter;
    }
    get shouldUseMobileProxyForMCP() {
        if (this._shouldUseMobileProxyForMCP === null) {
            if (isCometMobileBrowser()) {
                this._shouldUseMobileProxyForMCP = true;
            }
            else if (isCometBrowser()) {
                this._shouldUseMobileProxyForMCP = false;
            }
            else {
                // note: mobileProxy.isAvailable returns undefined during SSR
                const isAvailable = this.mobileProxy.isAvailable;
                if (isAvailable !== undefined) {
                    this._shouldUseMobileProxyForMCP = isAvailable;
                }
            }
        }
        return this._shouldUseMobileProxyForMCP ?? false;
    }
    async getMcpTools(serverName) {
        if (this.shouldUseMobileProxyForMCP) {
            const result = this.mobileProxy.bridge.get_mcp_tools({
                server_name: serverName,
            });
            result.catch((error) => {
                logger.error(`get_mcp_tools failed:`, error);
            });
            return result.then((x) => {
                return x.tools;
            });
        }
        return this.cometAdapter.getMcpTools(serverName);
    }
    async callMcpTool(payload, context) {
        if (this.shouldUseMobileProxyForMCP) {
            const result = this.mobileProxy.bridge.call_mcp_tool({
                params: payload,
            });
            result.catch((error) => {
                logger.error(`call_mcp_tool failed:`, error);
            });
            return result.then((x) => {
                return { content: x.result };
            });
        }
        return this.cometAdapter.callMcpTool(payload, context);
    }
    async getMcpStdioServers() {
        if (this.shouldUseMobileProxyForMCP) {
            const result = this.mobileProxy.bridge.get_mcp_servers();
            result.catch((error) => {
                logger.error(`get_mcp_servers failed:`, error);
            });
            return result.then((x) => {
                this.cometAdapter.getStore().getState().actions.setMcpStdioServers(x.servers);
                return x.servers;
            });
        }
        return this.cometAdapter.getMcpStdioServers();
    }
    async getInstalledDxts() {
        if (this.shouldUseMobileProxyForMCP) {
            // DXTs are only available in Comet browser
            return [];
        }
        return this.cometAdapter.getInstalledDxts();
    }
    async refreshDxtAndMcpState() {
        await Promise.all([this.getMcpStdioServers(), this.getInstalledDxts()]);
    }
    /**
     * Initialize MCP tools from window.initialMCPTools if available.
     * This is used in mobile-sidecar to pre-populate MCP tools before
     * the bridge's async data exchange completes.
     */
    initializeFromWindowTools(initialTools) {
        try {
            if (!initialTools?.servers)
                return;
            const store = this.cometAdapter.getStore();
            Object.entries(initialTools.servers).forEach(([key, value]) => {
                if (value && Array.isArray(value)) {
                    store.getState().actions.setMcpTools(key, value);
                }
            });
        }
        catch (error) {
            logger.error('Failed to initialize MCP tools from window', error);
        }
    }
}
//# sourceMappingURL=mcpAdapter.js.map